/* eslint-disable no-unused-vars */
import { useEffect, useCallback } from 'react';
import { useSignal } from '@preact/signals-react';
import { default as DatePicker } from 'react-datepicker';
import { getDayMonthYear } from 'utils';
// import { reportsActions } from 'store/actions';
import { TodayBtns } from './components/todayBtns/TodayBtns';
import { CalendarFooter } from './components/calendarFooter/CalendarFooter';
import { DateRangeInput } from './components/dateRangeInput/DateRangeInput';

import * as S from './DateRange.styled';

const setFirstDayOfMonth = () => {
  const today = new Date();
  today.setDate(1);
  return today;
};

function getStartOfWeek(date = new Date()) {
  const dayOfWeek = date.getDay();
  const difference = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const startOfWeek = new Date(date);
  startOfWeek.setDate(date.getDate() - difference);
  startOfWeek.setHours(0, 0, 0, 0);
  return startOfWeek;
}

function getLastMonthStart() {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date;
}

function getLastMonthEnd() {
  const date = new Date();
  date.setDate(1);
  // date.setHours(23, 59, 59, 999);
  date.setDate(0);
  return date;
}

// function getLastMonth() {
//   const currentDate = new Date();
//   const startOfCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
//   const endOfLastMonth = new Date(startOfCurrentMonth - 1);
//   const startOfLastMonth = new Date(endOfLastMonth.getFullYear(), endOfLastMonth.getMonth(), 1);
// }

function getDateDaysAgo(days) {
  const today = new Date();
  const pastDate = new Date(today.setDate(today.getDate() - days));
  return pastDate;
}

function createDate(dateFromOrTo) {
  let date;
  try {
    date = new Date(dateFromOrTo);
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }
  } catch (e) {
    date = setFirstDayOfMonth();
  }
  return date;
}

const getDataToConsole = (data) => {
  const year = data.getFullYear();
  const month = data.getMonth();
  const day = data.getDay();
  return `${year}/${month}/${day}`;
};

export const DateRange = ({
  dateFrom,
  dateTo,
  dateRangeName,
  onUpdateRangeName,
  onUpdateTimeRange,
}) => {
  const isCalendarOpen$ = useSignal(false);
  const typeOfOnChange$ = useSignal('Custom');
  const startDate$ = useSignal(createDate(dateFrom));
  const endDate$ = useSignal(createDate(dateTo));

  useEffect(() => {
    startDate$.value = createDate(dateFrom);
  }, [dateFrom]);

  useEffect(() => {
    endDate$.value = createDate(dateTo);
  }, [dateTo]);

  const calendarStartDate$ = useSignal(startDate$.value);
  const calendarEndDate$ = useSignal(endDate$.value);

  const footerStartDate$ = useSignal(getDayMonthYear(calendarStartDate$.value));
  const footerEndDate$ = useSignal(getDayMonthYear(calendarEndDate$.value));

  const typeOfCalendar$ = useSignal(dateRangeName);

  useEffect(() => {
    typeOfCalendar$.value = dateRangeName;
  }, [dateRangeName]);

  const onCalendarChange = useCallback((dates, btnType) => {
    let startOfPeriod;
    let endOfPeriod;

    // if (btnType === 'empty1' || btnType === 'empty2') return;
    if (btnType === 'Custom') {
      typeOfCalendar$.value = btnType;
      const [start, end] = dates;
      startOfPeriod = start;
      endOfPeriod = end;
    }
    let today = new Date();
    if (btnType === 'Today') {
      typeOfCalendar$.value = btnType;
      startOfPeriod = endOfPeriod = today;
    }
    if (btnType === 'Yesterday') {
      typeOfCalendar$.value = btnType;
      let yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      startOfPeriod = endOfPeriod = yesterday;
    }
    if (btnType === 'This Week') {
      typeOfCalendar$.value = btnType;
      startOfPeriod = getStartOfWeek();
      endOfPeriod = today;
    }
    if (btnType === 'This Month') {
      typeOfCalendar$.value = btnType;
      startOfPeriod = setFirstDayOfMonth();
      endOfPeriod = today;
    }
    if (btnType === 'Last Full Month') {
      typeOfCalendar$.value = btnType;
      startOfPeriod = getLastMonthStart();
      endOfPeriod = getLastMonthEnd();
    }
    if (btnType === 'Last 7 Days') {
      typeOfCalendar$.value = btnType;
      startOfPeriod = getDateDaysAgo(7);
      endOfPeriod = today;
    }
    if (btnType === 'Last 14 Days') {
      typeOfCalendar$.value = btnType;
      startOfPeriod = getDateDaysAgo(14);
      endOfPeriod = today;
    }
    if (btnType === 'Last Month') {
      typeOfCalendar$.value = btnType;
      startOfPeriod = getDateDaysAgo(30);
      endOfPeriod = today;
    }
    if (btnType !== 'Custom') {
      onUpdateRangeName(btnType);
      // dispatch(reportsActions.updateDateRangeName(btnType));
    }
    calendarStartDate$.value = startOfPeriod;
    calendarEndDate$.value = endOfPeriod;
    footerStartDate$.value = getDayMonthYear(startOfPeriod);
    footerEndDate$.value = getDayMonthYear(endOfPeriod);
    btnType !== 'Custom' && handleClickOkBtn(btnType);
    typeOfOnChange$.value = 'Custom';
  }, []);

  useEffect(() => {
    const fromDate = getDayMonthYear(startDate$.value);
    const toDate =
      !endDate$.value || endDate$.value === '' ? fromDate : getDayMonthYear(endDate$.value);
    onUpdateTimeRange(fromDate, toDate);
    // dispatch(reportsActions.updateTimeRange({ from: fromDate, to: toDate }));
  }, []);

  const handleScroll = () => {
    isCalendarOpen$.value = false;
  };

  useEffect(() => {
    if (isCalendarOpen$.value) {
      window.addEventListener('scroll', handleScroll, { once: true });
    }
    return () => {
      window.removeEventListener('scroll', handleScroll, { once: true });
    };
  }, [isCalendarOpen$.value]);

  const handleClickOkBtn = useCallback((btnType) => {
    if (!btnType) {
      typeOfCalendar$.value = 'Custom';
      onUpdateRangeName('Custom');
    }
    if (footerStartDate$.value) {
      startDate$.value = calendarStartDate$.value = new Date(footerStartDate$.value);
    } else {
      startDate$.value = calendarStartDate$.value;
      footerStartDate$.value = getDayMonthYear(calendarStartDate$.value);
    }

    if (footerEndDate$.value) {
      endDate$.value = calendarEndDate$.value = new Date(footerEndDate$.value);
    } else {
      if (calendarEndDate$.value) {
        endDate$.value = calendarEndDate$.value;
        footerEndDate$.value = getDayMonthYear(calendarEndDate$.value);
      } else {
        endDate$.value = calendarStartDate$.value;
        footerEndDate$.value = getDayMonthYear(calendarStartDate$.value);
      }
    }

    // if ()
    // endDate$.value = calendarEndDate$.value = new Date(
    //   footerEndDate$.value === '' ? footerStartDate$.value : footerEndDate$.value,
    // );
    isCalendarOpen$.value = false;
    const fromDate = getDayMonthYear(startDate$.value);
    const toDate = getDayMonthYear(endDate$.value);
    onUpdateTimeRange(fromDate, toDate);
  }, []);

  const handleClick = useCallback((event) => {
    if (typeof event.target.className !== 'string') return;
    if (event.target && event.target.className) {
      if (event?.target?.className?.includes('react-datepicker__day')) {
        typeOfOnChange$.value = 'Custom';
      }
    }
  }, []);

  const handleClose = useCallback(
    (e) => {
      e.preventDefault();
      isCalendarOpen$.value = false;
      calendarStartDate$.value = startDate$.value;
      calendarEndDate$.value = endDate$.value;
      footerStartDate$.value = getDayMonthYear(startDate$.value);
      footerEndDate$.value = getDayMonthYear(endDate$.value);

      typeOfCalendar$.value = dateRangeName;
    },
    [dateRangeName],
  );

  return (
    <>
      {isCalendarOpen$.value && <S.Slice onClick={handleClose} />}
      <div onClick={handleClick} style={{ display: 'flex' }}>
        <DateRangeInput
          //handleOpen={handleOpen}
          typeOfCalendar$={typeOfCalendar$}
          startDate$={startDate$}
          endDate$={endDate$}
          isCalendarOpen$={isCalendarOpen$}
        />
        <div style={{ zIndex: 2000000 }}>
          <DatePicker
            showPopperArrow={false}
            popperPlacement='bottom-start'
            popperModifiers={[
              {
                name: 'offset',
                options: {
                  offset: [-350, 15],
                },
              },
              {
                name: 'preventOverflow',
                options: {
                  rootBoundary: 'viewport',
                  tether: false,
                  altAxis: true,
                },
              },
            ]}
            showDisabledMonthNavigation
            maxDate={new Date()}
            customInput={<S.HiddenInput>hiddenInput</S.HiddenInput>}
            calendarClassName='manageCampaignDateRange'
            shouldCloseOnSelect={false}
            openToDate={calendarEndDate$.value}
            startDate={calendarStartDate$.value}
            endDate={calendarEndDate$.value}
            open={isCalendarOpen$.value}
            selectsRange
            selected={startDate$.value}
            onChange={(dates) => {
              onCalendarChange(dates, typeOfOnChange$.value);
            }}
            showPreviousMonths
            monthsShown={2}
            todayButton={
              <TodayBtns typeOfOnChange$={typeOfOnChange$} typeOfCalendar$={typeOfCalendar$} />
            }
          >
            <CalendarFooter
              footerStartDate$={footerStartDate$}
              footerEndDate$={footerEndDate$}
              handleClickOkBtn={handleClickOkBtn}
              handleClose={handleClose}
            />
          </DatePicker>
        </div>
      </div>
    </>
  );
};
